import { Navigate, Route, Routes } from "react-router-dom";
import { Enroll } from "@Light/components/enroll";
import { RequireLoggedIn } from "@Light/components/auth/RequireLoggedIn";
import { DashRoute } from "@Light/components/dash/DashRoute";
import { WithAccount } from "@Light/components/page/account/WithAccount";
import { AccountRoute } from "./account";
import { SupportRoute } from "./support";
import { DevicesRoute } from "./devices/DevicesRoute";
import { SandboxRoute } from "@Light/components/sandbox/SandboxRoute";
import { useScaffold } from "@Light/scaffold";
import { useAccount } from "@Light/components/page/account";
import { WithLocationPlan } from "@Light/utils/plan";

export const App: React.FC = () => {
  const scaffold = useScaffold();
  const Overlay = scaffold.page.overlay;
  return (
    <RequireLoggedIn redirect="/login/">
      <WithAccount>
        <Overlay>
          <LoggedInApp />
        </Overlay>
      </WithAccount>
    </RequireLoggedIn>
  );
};

export type LoggedInAppProps = {
  planUUID?: string;
};

export const LoggedInApp: React.FC<LoggedInAppProps> = ({ planUUID }) => {
  const account = useAccount();
  const enrollmentFinalized = account.enrollment.is_enrollment_finalized;
  const enrollRoute = enrollmentFinalized ? (
    <Route path="/enroll/*" element={<Navigate to="/dash/" replace />} />
  ) : (
    <Route path="/enroll/*" element={<Enroll planUUID={planUUID} />} />
  );
  const sandboxRoute = <Route path="/sandbox/*" element={<SandboxRoute />} />;
  if (!enrollmentFinalized) {
    return (
      <Routes>
        {enrollRoute}
        {sandboxRoute}
        <Route path="/*" element={<Navigate to="/enroll/" />} />
      </Routes>
    );
  }

  // These "service" routes are now supposed to be active when service has not
  // yet been activated but the customer has completed enrollment.
  const serviceRoutes = (
    <>
      <Route path="/dash/*" element={<DashRoute />} />
      <Route path="/account/*" element={<AccountRoute />} />
      <Route path="/support/*" element={<SupportRoute />} />
      <Route path="/devices/*" element={<DevicesRoute />} />
    </>
  );
  if (!account.enrollment.is_service_active) {
    return (
      <WithLocationPlan>
        <Routes>
          {enrollRoute}
          {sandboxRoute}
          {serviceRoutes}
          <Route path="/*" element={<Navigate to="/dash/" />} />
        </Routes>
      </WithLocationPlan>
    );
  }

  return (
    <WithLocationPlan>
      <Routes>
        {enrollRoute}
        {sandboxRoute}
        {serviceRoutes}
        <Route path="/*" element={<Navigate to="/dash/" />} />
      </Routes>
    </WithLocationPlan>
  );
};
